/**
 * ATHER opening slideshow.
 *
 * Built as a custom element on purpose: the Theme Editor tears a section out
 * of the DOM and injects a fresh one, and `disconnectedCallback` guarantees
 * the old timer, observer and listeners die with it. Nothing is registered on
 * `document`, so reordering or duplicating slides cannot stack timers up.
 *
 * Autoplay stops whenever it would be rude or pointless: the visitor touches
 * or focuses the slideshow, the tab goes to the background, the slideshow
 * scrolls off screen, or the visitor asks for reduced motion - in which case
 * it never starts and the fade is dropped to an instant swap.
 */
(function () {
  'use strict';

  var SWIPE_THRESHOLD = 40;

  function prefersReducedMotion() {
    return (
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  class AtherSlideshow extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-ather-slide]'));
      if (this.slides.length === 0) return;

      this.viewport = this.querySelector('[data-ather-viewport]');
      this.dots = Array.prototype.slice.call(this.querySelectorAll('[data-ather-dot]'));
      this.status = this.querySelector('[data-ather-status]');
      this.toggle = this.querySelector('[data-ather-toggle]');

      this.index = 0;
      this.timer = null;
      this.interval = Math.max(2, parseFloat(this.getAttribute('data-interval')) || 6) * 1000;
      this.autoplayWanted = this.getAttribute('data-autoplay') === 'true';
      this.paused = { user: false, offscreen: false, hidden: false };

      this.reduced = prefersReducedMotion();
      if (this.reduced) this.setAttribute('data-reduced', '');

      this.bind();
      this.go(0, { instant: true });

      if (this.slides.length > 1) this.observe();
      this.refreshToggle();
      this.play();
    }

    disconnectedCallback() {
      this.stop();
      if (this.io) this.io.disconnect();
      if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
      if (this.mq && this.onReduceChange) {
        if (this.mq.removeEventListener) this.mq.removeEventListener('change', this.onReduceChange);
        else if (this.mq.removeListener) this.mq.removeListener(this.onReduceChange);
      }
      this.ready = false;
    }

    /* ------------------------------------------------------------- wiring */

    bind() {
      var self = this;

      this.addEventListener('click', function (event) {
        var prev = event.target.closest('[data-ather-prev]');
        var next = event.target.closest('[data-ather-next]');
        var dot = event.target.closest('[data-ather-dot]');
        var toggle = event.target.closest('[data-ather-toggle]');

        if (prev || next || dot) {
          self.pauseByUser();
          if (prev) self.go(self.index - 1);
          if (next) self.go(self.index + 1);
          if (dot) self.go(self.dots.indexOf(dot));
        }

        if (toggle) {
          if (self.timer) {
            self.pauseByUser();
          } else {
            self.paused.user = false;
            self.play();
          }
          self.refreshToggle();
        }
      });

      this.addEventListener('keydown', function (event) {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        // In a right-to-left page the left arrow means "forward".
        var rtl = document.documentElement.dir === 'rtl';
        var forward = event.key === (rtl ? 'ArrowLeft' : 'ArrowRight');
        self.pauseByUser();
        self.go(self.index + (forward ? 1 : -1));
        event.preventDefault();
      });

      this.addEventListener('focusin', function () {
        self.pauseByUser();
        self.refreshToggle();
      });

      if (this.viewport) {
        var startX = null;
        var startY = null;

        this.viewport.addEventListener(
          'pointerdown',
          function (event) {
            startX = event.clientX;
            startY = event.clientY;
          },
          { passive: true }
        );

        this.viewport.addEventListener(
          'pointerup',
          function (event) {
            if (startX === null) return;
            var dx = event.clientX - startX;
            var dy = event.clientY - startY;
            startX = null;
            // Ignore anything that reads as a vertical scroll.
            if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dx) < Math.abs(dy)) return;
            var rtl = document.documentElement.dir === 'rtl';
            var forward = rtl ? dx > 0 : dx < 0;
            self.pauseByUser();
            self.go(self.index + (forward ? 1 : -1));
            self.refreshToggle();
          },
          { passive: true }
        );
      }

      this.onVisibility = function () {
        self.paused.hidden = document.hidden;
        self.paused.hidden ? self.stop() : self.play();
      };
      document.addEventListener('visibilitychange', this.onVisibility);

      if (typeof window.matchMedia === 'function') {
        this.mq = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.onReduceChange = function () {
          self.reduced = prefersReducedMotion();
          if (self.reduced) {
            self.setAttribute('data-reduced', '');
            self.stop();
          } else {
            self.removeAttribute('data-reduced');
            self.play();
          }
          self.refreshToggle();
        };
        if (this.mq.addEventListener) this.mq.addEventListener('change', this.onReduceChange);
        else if (this.mq.addListener) this.mq.addListener(this.onReduceChange);
      }
    }

    observe() {
      var self = this;
      if (!('IntersectionObserver' in window)) return;

      this.io = new IntersectionObserver(
        function (entries) {
          self.paused.offscreen = !entries[0].isIntersecting;
          self.paused.offscreen ? self.stop() : self.play();
        },
        { threshold: 0.25 }
      );
      this.io.observe(this);
    }

    /* -------------------------------------------------------------- moving */

    go(target, options) {
      var total = this.slides.length;
      var next = ((target % total) + total) % total;
      var instant = (options && options.instant) || this.reduced;

      for (var i = 0; i < total; i++) {
        var slide = this.slides[i];
        var active = i === next;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        // Keep a hidden slide out of the tab order.
        var focusables = slide.querySelectorAll('a, button');
        for (var f = 0; f < focusables.length; f++) {
          if (active) focusables[f].removeAttribute('tabindex');
          else focusables[f].setAttribute('tabindex', '-1');
        }
      }

      for (var d = 0; d < this.dots.length; d++) {
        this.dots[d].setAttribute('aria-current', d === next ? 'true' : 'false');
      }

      if (instant) this.setAttribute('data-instant', '');
      else this.removeAttribute('data-instant');

      this.index = next;
      this.preload(next + 1);

      if (this.status) {
        this.status.textContent = String(next + 1) + ' / ' + String(total);
      }
    }

    /**
     * Pulls the following slide's image in before it is shown, so a swipe or
     * an autoplay step never lands on an empty frame.
     */
    preload(at) {
      var total = this.slides.length;
      var slide = this.slides[((at % total) + total) % total];
      var img = slide.querySelector('img[loading="lazy"]');
      if (img) img.loading = 'eager';
    }

    /* ------------------------------------------------------------ autoplay */

    play() {
      if (this.timer) return;
      if (!this.autoplayWanted || this.reduced) return;
      if (this.slides.length < 2) return;
      if (this.paused.user || this.paused.offscreen || this.paused.hidden) return;

      var self = this;
      this.timer = window.setInterval(function () {
        self.go(self.index + 1);
      }, this.interval);
      this.refreshToggle();
    }

    stop() {
      if (!this.timer) return;
      window.clearInterval(this.timer);
      this.timer = null;
      this.refreshToggle();
    }

    pauseByUser() {
      this.paused.user = true;
      this.stop();
    }

    refreshToggle() {
      if (!this.toggle) return;
      var playing = !!this.timer;
      this.toggle.setAttribute('aria-pressed', playing ? 'false' : 'true');
      this.toggle.dataset.state = playing ? 'playing' : 'paused';
      var label = playing ? this.toggle.dataset.labelPause : this.toggle.dataset.labelPlay;
      if (label) this.toggle.setAttribute('aria-label', label);
    }
  }

  if (!customElements.get('ather-slideshow')) {
    customElements.define('ather-slideshow', AtherSlideshow);
  }
})();
